import type { QuestionType, SessionState, QuestionPhase } from './domain.js';
import type { BoardPublicQuestion } from './qaboard.js';
import type { SlideStyle } from './slides.js';

/** Erlaubte Live-Reaktions-Emojis (Teilnehmer -> Beamer). Serverseitig geprüft. */
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👏', '🔥', '😮', '🙌'] as const;
export function isReactionEmoji(e: unknown): e is string {
  return typeof e === 'string' && (REACTION_EMOJIS as readonly string[]).includes(e);
}

export interface SessionSounds {
  music?: string;
  countdown?: string;
  reveal?: string;
}

/**
 * Socket.IO-Protokoll zwischen API und den drei Clients (host/presenter/play).
 * Geteilt, damit Server und Frontend garantiert dasselbe Kontrakt verwenden.
 *
 * Rollen und Räume:
 *  - Teilnehmer  (play):      Beitritt über PIN + Nickname (+ Reconnect-Token).
 *  - Host        (host):      Steuerung; über Host-Cookie authentifiziert.
 *  - Presenter   (presenter): Beamer-Ansicht (read-only); über Host-Cookie oder Session-ID.
 */

// ─── Antwort-Payloads (typ-abhängig) ─────────────────────────────────────────

export type AnswerPayload =
  | { optionId: string } // single_choice, true_false
  | { optionIds: string[] } // multiple_choice, poll
  | { orderedOptionIds: string[] } // ordering
  | { pairs: Record<string, string> } // match_pair: leftOptionId -> rightId
  | { value: number } // numeric_estimate
  | { text: string } // free_text
  | { texts: string[] } // wordcloud
  | { x: number; y: number } // image_pin (Position in % des Bildes)
  | { ratings: number[] }; // scale (ein Wert je Aussage)

// ─── Rollen-gefilterte Sichten auf die aktuelle Frage ────────────────────────

/** Antwortoption ohne Preisgabe der Korrektheit (für Teilnehmer/Presenter vor Auflösung). */
export interface PublicOption {
  id: string;
  label: string;
  orderIndex: number;
  /** Optionales Antwortbild (lokaler /uploads-Pfad). */
  imagePath?: string | null;
}

/** Frage, wie sie während der Antwortphase gezeigt wird (ohne Lösung). */
export interface QuestionView {
  index: number;
  total: number;
  type: QuestionType;
  prompt: string;
  imagePath: string | null;
  timeLimitS: number;
  /** Optionen (bei ordering gemischt; bei match_pair die linke Spalte); leer bei numeric/free_text/… */
  options: PublicOption[];
  /** Nur match_pair: auswählbare rechte Seite (gemischt). */
  rights?: { id: string; label: string }[];
  /** Nur scale: Bewertungsskala für die Anzeige am Gerät. */
  scale?: { min: number; max: number; minLabel?: string; maxLabel?: string; statements: string[] };
  /** Nur spinner: Einträge des Glücksrads (oder Teilnehmer verwenden). */
  spinner?: { entries: string[]; useParticipants: boolean };
  /** Nur info: optionales lokales Video (Video-Folie), Wiedergabe auf dem Beamer. */
  video?: { src: string; loop: boolean; muted: boolean };
  /** Nur open_text (Brainstorming): dürfen Teilnehmende Beiträge hochvoten? */
  allowUpvotes?: boolean;
  /** Pro-Folie-Gestaltung (Hintergrund/Schrift/Farben/Ausrichtung). */
  style?: SlideStyle | null;
  /** Bonus-Runde: doppelte Punkte (Punktefaktor ≥ 2) – für Badge/Effekt. */
  bonus?: boolean;
}

/** Beitrag bei den Sammel-Typen (open_text / qa). */
export interface ContributionItem {
  id: string;
  text: string;
  votes: number;
  /** true, wenn der aktuelle Client diesen Beitrag hochgevotet hat. */
  mine?: boolean;
}

/** Verteilung der Antworten je Option (nach Auflösung / bei Umfrage). */
export interface OptionDistribution {
  optionId: string;
  label: string;
  count: number;
  isCorrect: boolean;
}

/** Aggregierte Skala-Auswertung je Aussage (für Live-Diagramm + Auflösung). */
export interface ScaleStatementAgg {
  label: string;
  average: number;
  responses: number;
  values: { value: number; count: number }[];
}
export interface ScaleAggregate {
  min: number;
  max: number;
  statements: ScaleStatementAgg[];
}

export interface RevealPayload {
  index: number;
  distribution: OptionDistribution[];
  /** Für numeric/free_text: die korrekte(n) Lösung(en) als Text. */
  correctText?: string;
  explanation?: string | null;
  /** Für Wordcloud: aggregierte Begriffe mit Häufigkeit. */
  words?: { text: string; count: number }[];
  /** Für scale: aggregierte Auswertung je Aussage. */
  scale?: ScaleAggregate;
  /** Für ranking: aggregierte Reihenfolge (Ø Position, aufsteigend = beliebter). */
  ranking?: { label: string; avg: number }[];
  /** Für image_pin: gesetzte Pins + Zielbereich (zur Anzeige nach Auflösung). */
  pins?: { points: { x: number; y: number; correct: boolean }[]; target: { x: number; y: number; radius: number } };
}

export interface ScoreboardRow {
  participantId: string;
  nickname: string;
  emoji: string;
  score: number;
  rank: number;
  /** Punktzuwachs aus der letzten Frage. */
  delta: number;
  /** Team-Zuordnung (nur im Team-Modus). */
  team?: string | null;
}

export interface LobbyParticipant {
  id: string;
  nickname: string;
  emoji: string;
  team?: string | null;
}

/** Aggregierte Team-Wertung (nur im Team-Modus). */
export interface TeamScoreRow {
  team: string;
  score: number;
  members: number;
  rank: number;
}

/** Summiert die Einzelpunkte je Team und sortiert absteigend. */
export function aggregateTeams(rows: ScoreboardRow[], teams: string[]): TeamScoreRow[] {
  const map = new Map<string, { score: number; members: number }>();
  for (const t of teams) map.set(t, { score: 0, members: 0 });
  for (const r of rows) {
    if (!r.team) continue;
    const e = map.get(r.team) ?? { score: 0, members: 0 };
    e.score += r.score;
    e.members += 1;
    map.set(r.team, e);
  }
  return [...map.entries()]
    .map(([team, e]) => ({ team, score: e.score, members: e.members }))
    .sort((a, b) => b.score - a.score)
    .map((t, i) => ({ ...t, rank: i + 1 }));
}

/** Persönliches Feedback an einen Teilnehmer nach der Auflösung. */
export interface PersonalResult {
  correct: boolean | null; // null bei Umfrage/Wordcloud
  pointsAwarded: number;
  totalScore: number;
  rank: number;
  streak: number;
}

/** Gesamtzustand, den ein Client beim (Wieder-)Beitritt erhält, um sich zu synchronisieren. */
/** Anzeigesprache (UI). Standard ist Deutsch. */
export type Locale = 'de' | 'en';

export interface SessionSnapshot {
  sessionId: string;
  state: SessionState;
  phase: QuestionPhase | null;
  /** Anzeigesprache dieser Session (vom Host festgelegt; Beamer & Teilnehmer folgen). */
  locale: Locale;
  /** Theme dieses Quiz (für Beamer/Teilnehmer konsistent). */
  themeId: string;
  themeOverrides: Record<string, string> | null;
  /** Eigene, hochgeladene Sound-Dateien (falls gesetzt) – ersetzen die Synth-Sounds. */
  sounds: SessionSounds | null;
  allowLateJoin: boolean;
  participantCount: number;
  /** Serverzeit (ms) beim Senden – für Uhrensynchronisation der Restzeit. */
  serverNow: number;
  /** Zeitstempel (ms), zu dem die Antwortphase endet (nur in phase=answering). */
  endsAt: number | null;
  question: QuestionView | null;
  reveal: RevealPayload | null;
  scoreboard: ScoreboardRow[] | null;
  lobby: LobbyParticipant[] | null;
  /** Nur open_text: bisher gesammelte Beiträge der aktuellen Frage. */
  contributions: ContributionItem[] | null;
  /** Team-Modus: Liste der Teamnamen (null = kein Team-Modus). */
  teams: string[] | null;
  /** Team des eigenen Geräts (nur Teilnehmer im Team-Modus). */
  myTeam?: string | null;
}

// ─── Event-Maps ──────────────────────────────────────────────────────────────

export interface ServerToClientEvents {
  'session:snapshot': (snap: SessionSnapshot) => void;
  'lobby:update': (p: { participants: LobbyParticipant[]; count: number }) => void;
  'question:countdown': (p: { index: number; secondsLeft: number }) => void;
  'question:show': (p: { question: QuestionView; endsAt: number; serverNow: number }) => void;
  'answer:count': (p: { answered: number; total: number }) => void;
  'question:reveal': (p: RevealPayload) => void;
  'scoreboard:show': (p: { rows: ScoreboardRow[] }) => void;
  'contributions:update': (p: { items: ContributionItem[] }) => void;
  /** Wordcloud: live wachsende Wörter während der Antwortphase. */
  'wordcloud:update': (p: { words: { text: string; count: number }[] }) => void;
  /** Skala: live aktualisierte Aggregate je Aussage während der Antwortphase. */
  'scale:update': (p: ScaleAggregate) => void;
  /** Umfrage (poll): live wachsende Stimmenverteilung während der Antwortphase. */
  'poll:update': (p: { distribution: OptionDistribution[] }) => void;
  'session:ended': (p: { podium: ScoreboardRow[]; hardestQuestions: { index: number; prompt: string; correctRatio: number }[] }) => void;
  'participant:result': (p: PersonalResult) => void;
  'participant:kicked': (p: { reason: string }) => void;
  /** Eigenständiges Q&A-Board: aktualisierte öffentliche Fragenliste. */
  'qaboard:update': (p: { questions: BoardPublicQuestion[] }) => void;
  'session:paused': () => void;
  'session:resumed': () => void;
  /** Live-Emoji-Reaktion eines Teilnehmers – fliegt über den Beamer. */
  'reaction:show': (p: { emoji: string; id: string }) => void;
  error: (p: { code: string; message: string }) => void;
}

export interface ClientToServerEvents {
  'participant:join': (
    p: { pin: string; nickname: string; emoji?: string; token?: string },
    ack: (r: { ok: true; token: string; participantId: string; emoji: string; team?: string | null; powerups?: number; snapshot: SessionSnapshot } | { ok: false; code: string; message: string }) => void,
  ) => void;
  'participant:answer': (p: { answer: AnswerPayload }, ack: (r: { ok: boolean; code?: string; message?: string }) => void) => void;
  /** open_text-Folie: einen Beitrag zur aktuellen Frage einreichen. */
  'participant:contribute': (p: { text: string }, ack: (r: { ok: boolean; code?: string; message?: string }) => void) => void;
  /** open_text-Folie: einen Beitrag hoch-/runter-voten (toggle). */
  'participant:upvote': (p: { contributionId: string }, ack: (r: { ok: boolean }) => void) => void;
  /** Live-Emoji-Reaktion senden (ephemer, keine Wertung). */
  'participant:reaction': (p: { emoji: string }) => void;
  /** Power-up für die aktuelle Frage aktivieren (z. B. Doppelte Punkte). */
  'participant:powerup': (
    p: { kind: 'double' | 'fiftyFifty' | 'freeze' },
    ack: (r: { ok: boolean; remaining: number; removedOptionIds?: string[]; freezeMs?: number }) => void,
  ) => void;

  'host:join': (p: { sessionId: string }, ack: (r: { ok: true; snapshot: SessionSnapshot } | { ok: false; code: string; message: string }) => void) => void;
  'host:start': (ack: (r: { ok: boolean; message?: string }) => void) => void;
  'host:next': (ack: (r: { ok: boolean; message?: string }) => void) => void;
  'host:skip': (ack: (r: { ok: boolean; message?: string }) => void) => void;
  'host:extendTime': (p: { seconds: number }, ack: (r: { ok: boolean }) => void) => void;
  'host:pause': (ack: (r: { ok: boolean }) => void) => void;
  'host:resume': (ack: (r: { ok: boolean }) => void) => void;
  'host:end': (ack: (r: { ok: boolean }) => void) => void;
  'host:kick': (p: { participantId: string }, ack: (r: { ok: boolean }) => void) => void;

  'presenter:join': (p: { sessionId: string }, ack: (r: { ok: true; snapshot: SessionSnapshot } | { ok: false; code: string; message: string }) => void) => void;

  // ─── Eigenständiges Q&A-Board (kein Login, eigener Code) ───────────────────
  'qaboard:join': (
    p: { code: string; voterToken: string },
    ack: (
      r:
        | { ok: true; boardId: string; title: string; questions: BoardPublicQuestion[]; votedIds: string[] }
        | { ok: false; message: string },
    ) => void,
  ) => void;
  'qaboard:ask': (p: { text: string }, ack: (r: { ok: boolean; code?: string; message?: string }) => void) => void;
  'qaboard:vote': (p: { questionId: string }, ack: (r: { ok: boolean }) => void) => void;
}

/** Länge der Beitritts-PIN. */
export const PIN_LENGTH = 6;
/** Dauer des Countdowns vor jeder Frage (Sekunden). */
export const COUNTDOWN_SECONDS = 3;
